import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
} from '@mlm/ui';
import {
  WRITE_OFF_REASONS,
  listBranchInventory,
  receiveInventoryBatch,
  writeOffInventoryBatch,
  type InventoryBatchKind,
  type InventoryBatchLineInput,
  type InventoryBatchSummary,
  type InventoryRow,
  type InventoryVariantRow,
  type WriteOffReason,
} from '../../lib/api';

export type InventoryBatchDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  branchId: string;
  branchName: string;
  branchCode: string;
  kind: InventoryBatchKind;
  /** Called with the new batch after a successful submit (for navigation/print). */
  onCreated?: (batch: InventoryBatchSummary) => void;
};

type DraftLine = {
  productId: string;
  /** Set when the line targets a specific variation (its own stock row). */
  variantId?: string;
  sku: string; // variant SKU when a variant line, else the product SKU
  name: string; // "Product — Variation" when a variant line
  currentQty: number;
  qty: string; // user-entered, stringly typed for input UX
};

/** Stable per-line key — a product line and its variant lines are distinct. */
const lineKey = (l: { productId: string; variantId?: string }) =>
  `${l.productId}:${l.variantId ?? ''}`;

/**
 * The "document" capture form for an admin receive shipment or write-off
 * report. Pick products from a searchable list, key qty per line, optional
 * batch-level memo + (write-off only) batch-level reason. Submits as a single
 * atomic batch through the receive-batch / writeoff-batch route.
 */
export function InventoryBatchDialog({
  open,
  onOpenChange,
  branchId,
  branchName,
  branchCode,
  kind,
  onCreated,
}: InventoryBatchDialogProps) {
  const qc = useQueryClient();
  const isReceive = kind === 'receive';

  const [lines, setLines] = useState<DraftLine[]>([]);
  const [search, setSearch] = useState('');
  const [memo, setMemo] = useState('');
  const [reason, setReason] = useState<WriteOffReason>('damaged');
  const [error, setError] = useState<string | null>(null);

  const inv = useQuery({
    queryKey: ['admin-inventory', branchId],
    queryFn: () => listBranchInventory(branchId),
    enabled: open,
  });

  function reset() {
    setLines([]);
    setSearch('');
    setMemo('');
    setReason('damaged');
    setError(null);
  }

  const linesByKey = useMemo(() => new Set(lines.map(lineKey)), [lines]);

  const searchResults = useMemo(() => {
    if (!inv.data) return [];
    const q = search.trim().toLowerCase();
    if (q.length < 1) return [];
    return inv.data
      .filter(
        (row) =>
          row.sku.toLowerCase().includes(q) ||
          row.name.toLowerCase().includes(q) ||
          row.variants.some(
            (v) => v.sku.toLowerCase().includes(q) || v.name.toLowerCase().includes(q),
          ),
      )
      .slice(0, 8);
  }, [inv.data, search]);

  function addProductLine(row: InventoryRow) {
    setLines((ls) => [
      ...ls,
      { productId: row.productId, sku: row.sku, name: row.name, currentQty: row.qty, qty: '' },
    ]);
    setSearch('');
    setError(null);
  }

  // Variant-bearing products REQUIRE a specific variation — its stock lives in a
  // per-variant row (POS sells and ignores the product-level count for them).
  function addVariantLine(row: InventoryRow, v: InventoryVariantRow) {
    setLines((ls) => [
      ...ls,
      {
        productId: row.productId,
        variantId: v.variantId,
        sku: v.sku,
        name: `${row.name} — ${v.name}`,
        currentQty: v.qty,
        qty: '',
      },
    ]);
    setSearch('');
    setError(null);
  }

  function removeLine(key: string) {
    setLines((ls) => ls.filter((l) => lineKey(l) !== key));
  }

  function setLineQty(key: string, qty: string) {
    setLines((ls) => ls.map((l) => (lineKey(l) === key ? { ...l, qty } : l)));
  }

  const validatedLines: { ok: boolean; lines: InventoryBatchLineInput[]; overdraft: string[] } =
    useMemo(() => {
      const out: InventoryBatchLineInput[] = [];
      const overdraft: string[] = [];
      let ok = lines.length > 0;
      for (const l of lines) {
        const n = Number(l.qty);
        const valid = l.qty.trim() !== '' && Number.isInteger(n) && n > 0;
        if (!valid) {
          ok = false;
          continue;
        }
        if (!isReceive && n > l.currentQty) {
          overdraft.push(l.sku);
          ok = false;
        }
        out.push({ productId: l.productId, variantId: l.variantId, qty: n });
      }
      return { ok, lines: out, overdraft };
    }, [lines, isReceive]);

  const totalUnits = useMemo(() => {
    let sum = 0;
    for (const l of lines) {
      const n = Number(l.qty);
      if (Number.isFinite(n) && n > 0) sum += n;
    }
    return sum;
  }, [lines]);

  const submit = useMutation({
    mutationFn: async () => {
      const trimmedMemo = memo.trim() || undefined;
      if (isReceive) {
        return receiveInventoryBatch(branchId, {
          lines: validatedLines.lines,
          memo: trimmedMemo,
        });
      }
      return writeOffInventoryBatch(branchId, {
        lines: validatedLines.lines,
        reason,
        memo: trimmedMemo,
      });
    },
    meta: { successMessage: 'Inventory batch saved.' },
    onSuccess: (batch) => {
      qc.invalidateQueries({ queryKey: ['admin-inventory', branchId] });
      qc.invalidateQueries({ queryKey: ['admin-inventory-batches', branchId] });
      qc.invalidateQueries({ queryKey: ['admin-inventory-logs', branchId] });
      onCreated?.(batch);
      reset();
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: string; message?: string } } };
      const code = e.response?.data?.error ?? 'request_failed';
      setError(code.replace(/_/g, ' '));
    },
  });

  const title = isReceive ? 'Receive shipment' : 'Write-off report';
  const description = isReceive
    ? `Restock for ${branchName}. The whole batch commits or rolls back together.`
    : `Formal deduction for ${branchName}. Captures one document covering all lines.`;
  const submitLabel = isReceive ? 'Receive' : 'Write off';

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between text-xs text-muted-foreground border-b pb-2">
          <span>
            Branch <span className="font-mono text-foreground">{branchCode}</span>
          </span>
          <span>
            {lines.length} {lines.length === 1 ? 'line' : 'lines'} · {totalUnits}{' '}
            {totalUnits === 1 ? 'unit' : 'units'}
          </span>
        </div>

        {/* Product picker */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Add product</label>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search SKU or product name…"
          />
          {search.trim().length >= 1 && (
            <div className="rounded-md border max-h-48 overflow-auto divide-y">
              {inv.isLoading ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">Loading…</p>
              ) : searchResults.length === 0 ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">No matches.</p>
              ) : (
                searchResults.map((row) =>
                  row.variants.length === 0 ? (
                    // Product-level line (no variations).
                    (() => {
                      const added = linesByKey.has(lineKey({ productId: row.productId }));
                      return (
                        <button
                          key={row.productId}
                          type="button"
                          disabled={added}
                          className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent disabled:opacity-50 disabled:hover:bg-transparent"
                          onClick={() => addProductLine(row)}
                        >
                          <span>
                            <span className="font-mono text-xs text-muted-foreground">{row.sku}</span>
                            <span className="ml-2">{row.name}</span>
                          </span>
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {added ? 'added' : `on-hand ${row.qty}`}
                          </span>
                        </button>
                      );
                    })()
                  ) : (
                    // Variant-bearing product: pick a specific variation (required).
                    <div key={row.productId}>
                      <div className="px-3 py-1.5 text-xs text-muted-foreground bg-muted/40">
                        <span className="font-mono">{row.sku}</span>
                        <span className="ml-2">{row.name}</span>
                        <span className="ml-2 italic">· pick a variation</span>
                      </div>
                      {row.variants.map((v) => {
                        const added = linesByKey.has(
                          lineKey({ productId: row.productId, variantId: v.variantId }),
                        );
                        return (
                          <button
                            key={v.variantId}
                            type="button"
                            disabled={added}
                            className="flex w-full items-center justify-between gap-2 px-3 py-2 pl-6 text-left text-sm hover:bg-accent disabled:opacity-50 disabled:hover:bg-transparent"
                            onClick={() => addVariantLine(row, v)}
                          >
                            <span>
                              <span className="font-mono text-xs text-muted-foreground">{v.sku}</span>
                              <span className="ml-2">{v.name}</span>
                            </span>
                            <span className="text-xs text-muted-foreground tabular-nums">
                              {added ? 'added' : `on-hand ${v.qty}`}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ),
                )
              )}
            </div>
          )}
        </div>

        {/* Lines */}
        {lines.length === 0 ? (
          <div className="rounded-md border border-dashed py-8 text-center text-sm text-muted-foreground">
            No lines yet. Search above to add products to this {isReceive ? 'shipment' : 'report'}.
          </div>
        ) : (
          <div className="rounded-md border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground border-b bg-muted/30">
                <tr>
                  <th className="px-3 py-2 font-medium">SKU</th>
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium text-right">On-hand</th>
                  <th className="px-3 py-2 font-medium w-28">Qty</th>
                  <th className="px-3 py-2 font-medium text-right">After</th>
                  <th className="px-3 py-2 w-8" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const key = lineKey(l);
                  const n = Number(l.qty);
                  const valid = l.qty.trim() !== '' && Number.isInteger(n) && n > 0;
                  const after = !valid
                    ? null
                    : isReceive
                      ? l.currentQty + n
                      : l.currentQty - n;
                  const overdraft = after !== null && after < 0;
                  return (
                    <tr key={key} className="border-b last:border-0">
                      <td className="px-3 py-2 font-mono text-xs">{l.sku}</td>
                      <td className="px-3 py-2">{l.name}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {l.currentQty}
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          value={l.qty}
                          onChange={(e) => setLineQty(key, e.target.value)}
                          inputMode="numeric"
                          placeholder="qty"
                          className="h-8"
                        />
                      </td>
                      <td
                        className={`px-3 py-2 text-right tabular-nums ${
                          overdraft ? 'text-destructive' : 'text-foreground'
                        }`}
                      >
                        {after === null ? '' : after}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => removeLine(key)}
                          className="text-muted-foreground hover:text-destructive text-sm"
                          aria-label="Remove line"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Reason (write-off only) */}
        {!isReceive && (
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Reason</label>
            <div className="flex flex-wrap gap-2">
              {WRITE_OFF_REASONS.map((r) => (
                <button
                  key={r.code}
                  type="button"
                  onClick={() => setReason(r.code)}
                  className={`px-3 py-1 rounded-md border text-sm ${
                    reason === r.code
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'hover:bg-accent'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Memo */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">
            {isReceive ? 'Memo / supplier reference' : 'Memo / additional notes'}
          </label>
          <Input
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder={
              isReceive ? 'e.g. Globex Phil DR #DLV-2026-0142' : 'e.g. Pallet drop in receiving'
            }
          />
        </div>

        {/* Overdraft warning */}
        {validatedLines.overdraft.length > 0 && (
          <p className="text-xs text-warning">
            Insufficient stock on: {validatedLines.overdraft.join(', ')}. Reduce the qty or remove
            the line.
          </p>
        )}

        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
            <Badge variant="destructive" className="mr-1.5">
              Error
            </Badge>
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submit.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!validatedLines.ok || submit.isPending}
            onClick={() => submit.mutate()}
          >
            {submit.isPending ? 'Saving…' : submitLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
